'use client'

import { Suspense } from 'react'
import { usePathname } from 'next/navigation'
import { Navbar } from "./custom/main/navbar"
import { RightSidebar } from "./custom/main/right-sidebar"
import { SiteFooter } from "./custom/main/footer"
import { PageSkeleton } from "./custom/main/page-skeleton"

export function Layout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  // Connect the Players is a single self-contained surface — no right-hand
  // rankings sidebar, no footer, and it fills the remaining viewport height
  // instead of scrolling with the rest of the page.
  const isFullBleed = pathname.startsWith('/games/connect-the-players')
  // /fpl and /stats don't need the right-hand rankings sidebar — reserving
  // its column (~300-340px + gap) on lg+ was eating usable width from
  // content (like the stats leaderboard rows) that wants the full row.
  const isSidebarless = pathname.startsWith('/fpl') || pathname.startsWith('/stats')

  if (isFullBleed) {
    return (
      // Fixed h-dvh + overflow-hidden on purpose: the message list
      // (ThreadPrimitive.Viewport) is the only element meant to scroll, and
      // assistant-ui's auto-scroll-to-bottom manages that viewport's own
      // scrollTop directly — it doesn't know how to scroll the page. If the
      // page itself became the scrolling element (e.g. via min-h-dvh with no
      // overflow-hidden), auto-scroll silently stops working.
      //
      // Zoom safety still holds without that: header/composer are
      // min-content-sized flex children, so the flex-1 message viewport is
      // what shrinks first — those two never get clipped, zooming just
      // shows less of the message list per screen (which still scrolls).
      <div className="h-dvh flex flex-col bg-background overflow-hidden">
        <Navbar />
        <main className="flex-1 min-h-0 pt-36 sm:pt-28 w-full">
          <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-background overflow-x-hidden">
      <Navbar />
      <main className="flex-1 pt-36 sm:pt-28 w-full px-3 sm:px-6">
        {isSidebarless ? (
          <div className="min-w-0 w-full">
            <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_340px] gap-16 lg:gap-6 items-start">
            <div className="min-w-0 w-full">
              <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
            </div>
            <RightSidebar />
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  )
}
